import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Upload, 
  Avatar, 
  Row, 
  Col, 
  Typography,
  Tabs,
  App,
  Spin,
  Alert,
} from 'antd';
import { 
  UserOutlined, 
  CameraOutlined, 
  LockOutlined
} from '@ant-design/icons';
import { useAuth } from '../services/auth';
import { userAPI } from '../services/api';
import passwordPolicyAPI from '../services/passwordPolicy';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
const IS_CUSTOMER_BUILD = process.env.REACT_APP_BUILD_TARGET === 'customer';
const getRoleLabel = IS_CUSTOMER_BUILD
  ? (role, t) => {
      switch (role) {
        case 'admin':
        case 'super_admin':
          return t('profile.user.role.admin', );
        case 'user':
        default:
          return t('profile.user.role.user', );
      }
    }
  : (role, t) => {
      switch (role) {
        case 'admin':
        case 'super_admin':
          return t('profile.user.role.admin', );
        case 'tenant_admin':
          return t('profile.user.role.tenant_admin', );
        case 'user':
        default:
          return t('profile.user.role.user', );
      }
    };

const { Title, Text } = Typography;

const Profile = () => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const [, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [passwordPolicy, setPasswordPolicy] = useState(null);
  const { user, updateUser } = useAuth();
  const location = useLocation();
  // (removed) const navigate = useNavigate();
  
  // 使用 ref 来管理 Form 实例
  const passwordFormRef = useRef(null);
  
  // 检查是否是强制密码修改
  const urlParams = new URLSearchParams(location.search);
  const isForcePasswordChange = urlParams.get('force') === 'true';
  const activeTab = urlParams.get('tab') || 'password';

  const fetchUserInfo = useCallback(async () => {
    try {
      setPageLoading(true);
      
      // 首先尝试从localStorage获取保存的用户信息
      const savedUserInfo = localStorage.getItem('userProfile');
      if (savedUserInfo) {
        const parsedInfo = JSON.parse(savedUserInfo);
        setUserInfo(parsedInfo);
      }
      
      // 然后尝试从API获取最新信息
      const response = await userAPI.getProfile();
      if (response.data && response.data.success) {
        const userData = response.data.data;
        setUserInfo(userData);
        // 保存到localStorage
        localStorage.setItem('userProfile', JSON.stringify(userData));
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
      
      // API失败时使用当前登录用户信息作为fallback
      if (user) {
        const fallbackInfo = {
          ...user,
          phone: user.phone || '',
          department: user.department || '',
          bio: user.bio || '',
        };
        setUserInfo(fallbackInfo);
      }
      
      message.warning(t('profile.message.load_fallback', ));
    } finally {
      setPageLoading(false);
    }
  }, [user, message, t]);

  useEffect(() => {
    fetchUserInfo();
    loadPasswordPolicy();
  }, [fetchUserInfo]);

  const loadPasswordPolicy = async () => {
    try {
      const response = await passwordPolicyAPI.getPasswordPolicy();
      if (response.data && response.data.success) {
        setPasswordPolicy(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load password policy:', error);
    }
  };



  const handlePasswordChange = async (values) => {
    setPasswordLoading(true);
    try {
      const response = await userAPI.changePassword({
        old_password: values.oldPassword,
        new_password: values.newPassword
      });
      
      if (response.data && response.data.success) {
        message.success(t('profile.password.message.change_success', ));
        passwordFormRef.current?.resetFields();
        
        // 如果是强制密码修改，修改成功后跳转到仪表板
        if (isForcePasswordChange) {
          message.success(t('profile.password.message.force_change_success', ));
          window.location.replace('/dashboard');
        }
      } else {
        throw new Error(response.data?.message || t('profile.password.message.change_failed', ));
      }
    } catch (error) {
      console.error('密码修改失败:', error);
      
      if (error.response?.status === 404) {
        message.error(t('profile.password.message.user_not_found_or_api_missing', ));
      } else if (error.response?.status === 401) {
        message.error(t('profile.password.message.incorrect_current_password', ));
      } else if (error.response?.status === 400) {
        // 显示具体的密码要求
        let errorMsg = t('profile.password.message.new_password_not_meet_requirements', );
        if (passwordPolicy) {
          const requirements = passwordPolicyAPI.formatPasswordRequirements(passwordPolicy);
          if (requirements.length > 0) {
            errorMsg += t('profile.password.message.requirements_prefix', ) + requirements.join('、');
          }
        }
        message.error(errorMsg);
      } else {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || t('common.unknown_error', );
        message.error(t('profile.password.message.change_failed_with_reason', { error: errorMessage + errorMessage }));
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleAvatarChange = (info) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }
    if (info.file.status === 'done') {
      message.success(t('profile.avatar.upload_success', ));
      setLoading(false);
      
      // 从响应中获取新的头像URL
      const response = info.file.response;
      if (response && response.success && response.data && response.data.avatar_url) {
        const newAvatarUrl = response.data.avatar_url;
        
        // 更新本地用户信息
        const updatedUserInfo = { ...userInfo, avatar: newAvatarUrl };
        setUserInfo(updatedUserInfo);
        
        // 更新localStorage中的用户信息
        localStorage.setItem('userProfile', JSON.stringify(updatedUserInfo));
        
        // 更新认证上下文中的用户信息，这样右上角头像会立即更新
        if (updateUser) {
          updateUser({ ...user, avatar: newAvatarUrl });
        }
      } else {
        // 如果没有从响应中获取到头像URL，则刷新用户信息
        fetchUserInfo();
      }
    } else if (info.file.status === 'error') {
      message.error(t('profile.avatar.upload_failed', ));
      setLoading(false);
    }
  };

  const uploadProps = {
    name: 'avatar',
    action: '/api/users/avatar',
    headers: {
      authorization: `Bearer ${localStorage.getItem('token')}`,
    },
    onChange: handleAvatarChange,
    beforeUpload: (file) => {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        message.error(t('profile.avatar.invalid_type', ));
        return false;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error(t('profile.avatar.too_large', ));
        return false;
      }
      return true;
    },
  };

  if (pageLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>{t('profile.loading', )}</div>
      </div>
    );
  }

  // 确保有用户信息后再渲染表单
  if (!userInfo && !user) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Alert
          message={t('profile.error.title', )}
          description={t('profile.error.description', )}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={24}>
        <Col span={8}>
          <Card>
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Upload {...uploadProps} showUploadList={false}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Avatar 
                    size={120} 
                    icon={<UserOutlined />}
                    src={userInfo?.avatar}
                    style={{ cursor: 'pointer' }}
                  />
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    background: '#1890ff',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}>
                    <CameraOutlined style={{ color: 'white', fontSize: 16 }} />
                  </div>
                </div>
              </Upload>
              <Title level={4} style={{ marginTop: 16, marginBottom: 4 }}>
                {userInfo?.name || user?.name || t('profile.user.default_name', )}
              </Title>
              <Text type="secondary">{userInfo?.email || user?.email}</Text>
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">{t('profile.user.role_label', )}</Text>
                <Text>
                  {getRoleLabel(userInfo?.role || user?.role, t)}
                </Text>
              </div>
              {!IS_CUSTOMER_BUILD ? (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">{t('profile.user.tenant_label', )}</Text>
                  <Text>{userInfo?.tenant_name || t('profile.user.default_tenant', )}</Text>
                </div>
              ) : null}
              {userInfo?.phone && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">{t('profile.user.phone_label', )}</Text>
                  <Text>{userInfo.phone}</Text>
                </div>
              )}
              {userInfo?.department && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">{t('profile.user.department_label', )}</Text>
                  <Text>{userInfo.department}</Text>
                </div>
              )}
            </div>
          </Card>
        </Col>

        <Col span={16}>
          <Card>
            <Tabs 
              activeKey={activeTab}
              items={[
                {
                  key: 'password',
                  label: <span><LockOutlined />{t('profile.password.tab_title', )}</span>,
                  children: (
                    <div>
                      {isForcePasswordChange ? (
                        <Alert
                          message={t('profile.password.force_change.title', )}
                          description={t('profile.password.force_change.description', )}
                          type="error"
                          showIcon
                          style={{ marginBottom: 16 }}
                        />
                      ) : (
                        <Alert
                          message={t('profile.password.security_hint.title', )}
                          description={t('profile.password.security_hint.description', )}
                          type="warning"
                          showIcon
                          style={{ marginBottom: 16 }}
                        />
                      )}
                      {/* 显示密码策略要求 */}
                      {passwordPolicy && (
                        <Alert
                          message={t('profile.password.policy_requirements.title', )}
                          description={
                            <div>
                              <div>{t('profile.password.policy_requirements.intro', )}</div>
                               <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                                 {passwordPolicyAPI.formatPasswordRequirements(passwordPolicy).map((req, index) => (
                                   <li key={index}>{req}</li>
                                 ))}
                                 {passwordPolicy.prevent_reuse > 0 && (
                                  <li>{t('profile.password.policy_requirements.prevent_reuse', { count: passwordPolicy.prevent_reuse })}</li>
                                 )}
                                 {passwordPolicy.max_age > 0 && (
                                  <li>{t('profile.password.policy_requirements.max_age', { days: passwordPolicy.max_age })}</li>
                                 )}
                               </ul>
                             </div>
                           }
                           type="info"
                           showIcon
                           style={{ marginBottom: 16 }}
                         />
                      )}
                      
                      <Form
                        ref={passwordFormRef}
                        layout="vertical"
                        onFinish={handlePasswordChange}
                        style={{ maxWidth: 400 }}
                      >
                        <Form.Item
                          label={t('profile.password.form.current_password.label')}
                          name="oldPassword"
                          rules={[{ required: true, message: t('profile.password.form.current_password.required') }]}
                        >
                          <Input.Password placeholder={t('profile.password.form.current_password.placeholder')} />
                        </Form.Item>

                        <Form.Item
                          label={t('profile.password.form.new_password.label')}
                          name="newPassword"
                          rules={[
                            { required: true, message: t('profile.password.form.new_password.required') },
                            { 
                              min: passwordPolicy?.min_length || 6, 
                              message: t('profile.password.form.new_password.min_length', { min: passwordPolicy?.min_length || 6 })
                            },
                            {
                              validator: async (_, value) => {
                                if (!value) return Promise.resolve();
                                
                                // 使用后端API验证密码
                                try {
                                  const response = await passwordPolicyAPI.validatePassword(value);
                                  if (response.data && response.data.success) {
                                    const { valid, errors } = response.data.data;
                                    if (!valid && errors && errors.length > 0) {
                                      return Promise.reject(new Error(errors.join('；')));
                                    }
                                  }
                                  return Promise.resolve();
                                } catch (error) {
                                  // 如果API调用失败，使用本地验证作为fallback
                                  if (passwordPolicy) {
                                    const isValid = passwordPolicy.ValidatePassword ? 
                                      passwordPolicy.ValidatePassword(value) : 
                                      true;
                                    if (!isValid) {
                                      const requirements = passwordPolicyAPI.formatPasswordRequirements(passwordPolicy);
                                      return Promise.reject(new Error(t('profile.password.form.new_password.not_meet_policy', { requirements: requirements.join('；') })));
                                    }
                                  }
                                  return Promise.resolve();
                                }
                              }
                            }
                          ]}
                        >
                          <Input.Password placeholder={t('profile.password.form.new_password.placeholder')} />
                        </Form.Item>

                        <Form.Item
                          label={t('profile.password.form.confirm_password.label')}
                          name="confirmPassword"
                          dependencies={["newPassword"]}
                          rules={[
                            { required: true, message: t('profile.password.form.confirm_password.required') },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (!value || getFieldValue('newPassword') === value) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(new Error(t('profile.password.form.confirm_password.mismatch')));
                              },
                            }),
                          ]}
                        >
                          <Input.Password placeholder={t('profile.password.form.confirm_password.placeholder')} />
                        </Form.Item>

                        <Form.Item>
                          <Button 
                            type="primary" 
                            htmlType="submit" 
                            loading={passwordLoading}
                            icon={<LockOutlined />}
                          >
                            {t('profile.password.form.submit', )}
                          </Button>
                        </Form.Item>
                      </Form>
                    </div>
                  )
                }
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Profile;
